/*
Copyright 2026 Symbiotek

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type MatrixClient } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

import { initClient } from "../utils/matrix";
import { type Session } from "../ClientContext";

/**
 * Credentials handed back by the guest provisioning service.
 */
interface GuestCredentials {
  user_id: string;
  device_id: string;
  access_token: string;
  home_server: string;
}

/**
 * Creates a throwaway account through an external provisioning service and
 * starts a client session on it.
 *
 * Element Call normally creates guest accounts itself, by driving the Matrix
 * registration API. That is not possible against a homeserver which delegates
 * authentication to Matrix Authentication Service: Synapse rejects all client
 * registration ("Only m.login.application_service registrations are allowed")
 * and MAS deliberately implements no legacy registration endpoint. Deployments
 * in that situation can point `guest_provisioning_url` at a service which
 * creates the account server-side and returns a ready-to-use session.
 *
 * @param provisioningUrl The service to ask for a guest account.
 * @param homeserverUrl The homeserver the account belongs to.
 * @param displayName The display name the guest chose.
 * @param oldClient An already authenticated client, which is logged out first.
 */
export async function provisionGuestUser(
  provisioningUrl: string,
  homeserverUrl: string,
  displayName: string,
  oldClient?: MatrixClient,
): Promise<[MatrixClient, Session]> {
  let response: Response;
  try {
    response = await fetch(provisioningUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName }),
    });
  } catch (e) {
    logger.error("Guest provisioning request failed", e);
    throw new Error("Could not reach the guest account service");
  }

  if (!response.ok) {
    logger.error(
      `Guest provisioning service returned ${response.status}`,
      await response.text().catch(() => ""),
    );
    throw new Error(
      response.status === 429
        ? "Too many guests have joined from here recently. Please try again later."
        : "Could not create a guest account",
    );
  }

  /* eslint-disable camelcase */
  const { user_id, access_token, device_id } =
    (await response.json()) as GuestCredentials;

  // To not confuse the rust crypto sessions we need to logout the old client
  // before initializing the new one.
  await oldClient?.logout(true);
  const client = await initClient(
    {
      baseUrl: homeserverUrl,
      accessToken: access_token,
      userId: user_id,
      deviceId: device_id,
    },
    false,
  );

  // The provisioning service already set the display name on the account, but
  // the local copy is only populated once the profile has been fetched, so seed
  // it here to avoid the name flickering in.
  const user = client.getUser(client.getUserId()!)!;
  user.setRawDisplayName(displayName);
  user.setDisplayName(displayName);

  return [
    client,
    {
      user_id,
      device_id,
      access_token,
      passwordlessUser: true,
    },
  ];
  /* eslint-enable camelcase */
}
